import { useMediaQuery } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { ContentByPropertyMap } from '../../data/sanity/types';
import { convertEntriesToMappedListsByProperty } from '../../data/sanity/utils';
import { getDefaultPax, getUrl } from '../../dtos/own/recipeHelpers';
import { buildRecipe } from '../../dtos/smart-search/helpers';
import { useLocalStorageState } from '../../hooks/useLocalStorageState/useLocalStorageState';
import { useLogService } from '../../hooks/useLogService';
import { useTranslate } from '../../hooks/useTranslate';
import { useAmelia } from '../../providers/AmeliaProvider';
import { useMarket } from '../../providers/MarketProvider';
import { useRedirectContext } from '../../providers/RedirectProvider';
import { ContentService } from '../../services/ContentService/ContentService';
import { CategoryType } from '../../services/ContentService/ContentService.types';
import { propertiesAsStrings } from '../../utils/helpers';
import { getLocalizedUrl } from '../../utils/markets';
import { CATEGORY_ICONS } from './DestinationSearch.constants';
import type {
  DestinationOptionType,
  GroupedOptions,
  LocalStorageResult,
  Option,
} from './DestinationSearch.types';
import {
  checkForHumor,
  getDestinationIdentifier,
  getOptionAddedToTopLastSearches,
  getOptionRemovedFromTopLastSearches,
  mapOptionsGroupedByCategoryLabel,
  topFourResultsByCategory,
} from './DestinationSearch.utils';

export const useDestinationSearch = (
  tripPlannerExtraQueryParams?: Record<string, string>
) => {
  const { t } = useTranslate();
  const { open } = useAmelia();
  const { logEvent } = useLogService();
  const { redirectTo } = useRedirectContext();
  const market = useMarket();
  const isDesktop = useMediaQuery('(min-width: 900px)');
  const inputRef = useRef<HTMLInputElement>(null);
  const [destination, setDestination] = useState<Option | null>(null);
  const [query, setQuery] = useState('');
  const [forceNoResults, setForceNoResults] = useState(false);
  const [localStorageValue, setLocalStorageValue] = useLocalStorageState<
    LocalStorageResult[]
  >('searchedOptions', undefined, {
    ttl: null,
  });
  const [lastOptionsGroupedByCategory, setLastOptionsGroupedByCategory] =
    useState<
      ContentByPropertyMap<DestinationOptionType, 'categoryType'> | undefined
    >(undefined);
  const shouldSkipNextRedirect = useRef(false);

  const skipNextRedirect = () => {
    shouldSkipNextRedirect.current = true;
  };

  const handleSelection = (option: Option) => {
    if (shouldSkipNextRedirect.current) {
      shouldSkipNextRedirect.current = false;
      return;
    }

    if (option.category === 'amelia') {
      open(
        'search-results',
        t('explore.amelia.searchPromptSuggestionChat', {
          searchText: option.label,
        })
      );

      logEvent('amelia_search_banner_clicked', option.label);

      return;
    }

    if (option.categoryType === 'RecentSearches') {
      logEvent('searcher_last_searches_option_selected', undefined, {
        ...propertiesAsStrings(option),
      });
    } else {
      logEvent('marketplace_search_option_selected', option.label, {
        ...propertiesAsStrings(option),
        searchText: query,
        query,
        isDesktop: isDesktop.toString(),
        indexOfType: option.indexOfType.toString(),
      });
    }

    setLocalStorageValue(
      getOptionAddedToTopLastSearches(option, localStorageValue)
    );

    if (option.polygonId && option.polygonType === 'country' && option.slug) {
      const link = getLocalizedUrl(market, `/places/${option.slug}`);
      if (link) {
        redirectTo(link);
        return;
      }
    }

    redirectTo(
      getUrl(
        buildRecipe(getDestinationIdentifier(option), getDefaultPax()),
        undefined,
        undefined,
        sessionStorage.getItem('pax') ? undefined : 'pax',
        market,
        undefined,
        undefined,
        tripPlannerExtraQueryParams
      )
    );
  };

  useEffect(() => {
    if (!destination) {
      return;
    }

    handleSelection(destination);
  }, [destination]);

  const isValidDestinationItem = (
    item: DestinationOptionType | undefined
  ): boolean => Boolean(item?.label && item.category && item.categoryType);

  const getFirstValidDestination = (
    groupedItems: GroupedOptions[]
  ): DestinationOptionType | null => {
    const firstItem = groupedItems[0]?.items[0];
    return isValidDestinationItem(firstItem) ? firstItem! : null;
  };

  const onSubmit = (groupedItems: GroupedOptions[] = []) => {
    if (destination) {
      handleSelection(destination);
      logEvent('inspire_search_banner_search_button_clicked', undefined, {
        ...propertiesAsStrings(destination),
      });
      return;
    }

    const firstValidItem = getFirstValidDestination(groupedItems);
    if (firstValidItem) {
      setDestination({ ...firstValidItem, indexOfType: 0 });
      return;
    }

    setForceNoResults(true);
    inputRef.current?.focus();
  };

  const setDestinationSearchOptions = (
    localStorageData: LocalStorageResult[]
  ) => {
    setLastOptionsGroupedByCategory(
      mapOptionsGroupedByCategoryLabel(
        localStorageData.map((res) => res.option)
      )
    );

    setForceNoResults(true);
  };

  const onRemove = (label: string): void => {
    if (!localStorageValue) {
      return;
    }

    logEvent('searcher_last_searches_option_removed', undefined, {
      label,
    });

    const updatedLastOptions = getOptionRemovedFromTopLastSearches(
      label,
      localStorageValue
    );

    setLocalStorageValue(updatedLastOptions);
    setLastOptionsGroupedByCategory(
      mapOptionsGroupedByCategoryLabel(
        updatedLastOptions.map((res) => res.option)
      )
    );
  };

  return {
    inputRef,
    destination,
    setDestination,
    query,
    setQuery,
    forceNoResults,
    onSubmit,
    setDestinationSearchOptions,
    localStorageValue,
    setLocalStorageValue,
    onRemove,
    lastOptionsGroupedByCategory,
    skipNextRedirect,
  };
};

export const useSearchResults = (searchText: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [resultsMap, setResultsMap] =
    useState<ContentByPropertyMap<DestinationOptionType, 'categoryType'>>();
  const debounceTime = Math.min(800, searchText.length * 200);
  const [debouncedSearchText] = useDebounce(searchText, debounceTime);
  const weHadEnoughFun = useRef(false);

  const market = useMarket();
  const { t } = useTranslate();
  const { logEvent } = useLogService();

  const isTyping = useMemo(
    () => debouncedSearchText !== searchText,
    [debouncedSearchText, searchText]
  );

  // Fetch the search results whenever searchText changes
  useEffect(() => {
    if (debouncedSearchText && debouncedSearchText.length > 1) {
      if (!weHadEnoughFun.current) {
        const isHumor = checkForHumor(debouncedSearchText);

        if (isHumor) {
          weHadEnoughFun.current = true;
          return;
        }
      }

      setIsLoading(true);

      // Fetch suggestions from multiple sources
      const fetchSuggestions = async () => {
        try {
          const results: DestinationOptionType[] = [];
          const [responseDestinationsAndHotels] = await Promise.allSettled([
            ContentService.getCategorizedAutocompleteSuggestions(
              debouncedSearchText,
              market.host
            ),
          ]);

          if (responseDestinationsAndHotels.status === 'fulfilled') {
            results.push(
              ...topFourResultsByCategory(responseDestinationsAndHotels.value)
            );
          }

          setResultsMap(
            convertEntriesToMappedListsByProperty(results, 'categoryType')
          );

          logEvent('query_searched', debouncedSearchText, {
            query: debouncedSearchText,
          });
        } finally {
          setIsLoading(false);
        }
      };

      fetchSuggestions();
    }
  }, [debouncedSearchText, market, t]);

  return {
    isLoading,
    isTyping,
    resultsMap,
  };
};

export const useGroupedItems = (
  resultsMap:
    | ContentByPropertyMap<DestinationOptionType, 'categoryType'>
    | undefined
): GroupedOptions[] => {
  const { t } = useTranslate();

  return Object.entries(resultsMap ?? []).map(([categoryType, items]) => {
    const { category, label, tooltip } = items[0]!;

    return {
      label: t(`explore.searchResultCategory.${categoryType}`, {
        categoryType,
        category,
        label,
        tooltip,
      }),
      items,
      icon: CATEGORY_ICONS[categoryType as CategoryType],
    };
  });
};
